import { Box, CircularProgress, FormControl, InputAdornment, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';

import { use3DViewerStore } from 'features/seismic-3d/stores/use3DViewerStore';
import { GridsListRow } from './GridsListRow';
import { useGetGridsIntersectingInGeom } from 'features/seismic-3d/api/useSeismic3DGridController';
import { AmplitudeDomain } from 'features/seismic-3d/models/enums/AmplitudeDomain';
import { IGridXyzBasicInfo } from 'features/seismic-3d/models/interfaces/IGridXyzBasicInfo';

export type GridsListProps = {
    volumeToken: number
};

export const GridsList = () => {
    const { geomWkt, srid, amplitudeDomain } = use3DViewerStore(state => ({
        geomWkt: state.mainFeatureGeomData?.GeomWKT,
        srid: state.mainFeatureSrid,
        amplitudeDomain: state.domain,
    }));

    const { data: grids, isLoading } = useGetGridsIntersectingInGeom(geomWkt, srid, srid, amplitudeDomain);
    const [ nameFilter, setNameFilter ] = useState('');
    const [ domainFilter, setDomainFilter ] = useState('');

    const filteredGrids = useMemo(() => grids?.filter(grid => {
        const matchesName = nameFilter === '' || grid.Name.toLowerCase().includes(nameFilter.toLowerCase());
        const matchesDomain = domainFilter === '' || grid.Domain === (parseInt(domainFilter) as AmplitudeDomain);
        return matchesName && matchesDomain;
    }), [domainFilter, grids, nameFilter]);

    const [ visibleGrids, setVisibleGrids ] = useState<IGridXyzBasicInfo[]>([]);

    const handleGridVisibilityChange = useCallback((grid: IGridXyzBasicInfo, visible: boolean) => {
        if (visible) {
            if (!visibleGrids.includes(grid)) {
                setVisibleGrids(current => [...current, grid]);
            }
        }
        else {
            const index = visibleGrids.indexOf(grid);
            if (index >= 0) {
                setVisibleGrids(current => {
                    const newArray = [...current];
                    newArray.splice(index, 1);
                    return newArray;
                });
            }
        }
    }, [visibleGrids]);

    return (
        <Fragment>
            <Stack gap={1} padding='10px'>
                <Stack direction='row' gap={2}>
                    <TextField
                        placeholder='Search a grid'
                        size='small'
                        InputProps={{
                            endAdornment: <InputAdornment position="end"><SearchIcon/></InputAdornment>
                        }}
                        value={nameFilter}
                        onChange={(event) => setNameFilter(event.target.value)}
                    />
                    <FormControl sx={{minWidth: '120px'}} size='small'>
                        <InputLabel id='grid-filter-domain-input-label'>Domain</InputLabel>
                        {
                            amplitudeDomain !== null &&
                            <Select
                                labelId='grid-filter-domain-input-label'
                                value={domainFilter}
                                onChange={(evt) => setDomainFilter(evt.target.value)}
                                label='Domain'
                            >
                                <MenuItem value={''}>All</MenuItem>
                                {
                                    <MenuItem value={amplitudeDomain} defaultChecked>{ amplitudeDomain === AmplitudeDomain.Depth ? 'Depth' : 'Time' }</MenuItem>
                                }
                                <MenuItem value={AmplitudeDomain.Others}>Others</MenuItem>
                            </Select>
                        }
                    </FormControl>
                </Stack>

                <TableContainer>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{width: '50px'}}>
                                    Select
                                </TableCell>
                                <TableCell sx={{width: '50px'}}>
                                    Options
                                </TableCell>
                                <TableCell sx={{width: '300px'}}>
                                    Name
                                </TableCell>
                                <TableCell sx={{width: '300px'}}>
                                    Domain
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                isLoading
                                &&
                                <TableCell colSpan={3}>
                                    <Stack direction='row' gap={1} sx={{alignItems: 'center'}}>
                                        <h3>Loading...</h3> <CircularProgress size={25}/>
                                    </Stack>
                                </TableCell>
                            }
                            {
                                filteredGrids
                                &&
                                filteredGrids.map(grid => (
                                    <GridsListRow key={grid.Token} grid={grid} visible={visibleGrids.includes(grid)} onChangeVisibility={(visible) => handleGridVisibilityChange(grid, visible)}/>
                                ))
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            </Stack>
        </Fragment>
    );
};
